/**
 * or-group-disable.ts — czysta logika wzajemnego WYGASZANIA filtrów kolumn ZŁOŻONYCH (grupa OR).
 *
 * Kolumna złożona (np. „Imię i nazwisko") deklaruje `headerFilter.orGroupFields` = slugi pól
 * składowych. Backend rozwija filtr tej kolumny na warunek po tych polach (OR/AND per operator).
 * Front musi zapewnić, że NARAZ aktywny jest tylko filtr kolumny złożonej ALBO filtr pola
 * składowego — inaczej oba doleciałyby do zapytania i podwójnie zawężały wynik. Stąd:
 *  - wygaszanie WIZUALNE (`computeOrGroupDisabled`) — komórka wygaszona pokazuje frazę drugiej strony
 *    read-only,
 *  - czyszczenie STANU (`clearOrGroupPeers`) — zapis jednej strony usuwa wpis drugiej.
 *
 * Bez Reacta (testowalne w izolacji). Relacje wyprowadzone z PEŁNEJ listy kolumn (nie tylko
 * widocznych), by czyszczenie działało też dla pola składowego ukrytego z aktywnym filtrem.
 */

import type { ColumnHeaderFilter, ColumnHeaderFilterConfig, ColumnHeaderFilters } from './types';

type OrGroupColumn = { id: string; headerFilter?: ColumnHeaderFilterConfig };

export interface OrGroupRelations {
  /** id kolumny złożonej → id kolumn pól składowych (istniejących; self-referencja wykluczona). */
  compositeToMemberIds: Record<string, string[]>;
  /** id kolumny pola składowego → id jego kolumny złożonej. */
  memberIdToCompositeId: Record<string, string>;
}

/**
 * Zbuduj relacje kolumna-złożona ↔ pola-składowe. Slug pola składowego (`orGroupFields[]`) mapowany
 * na id kolumny przez efektywny slug kolumny `fieldSlug ?? id`. Self-referencja (slug pola == id
 * kolumny złożonej) i slugi bez odpowiadającej kolumny są pomijane.
 */
export function buildOrGroupRelations(columns: readonly OrGroupColumn[]): OrGroupRelations {
  const slugToColumnId: Record<string, string> = {};
  for (const col of columns) {
    slugToColumnId[col.headerFilter?.fieldSlug ?? col.id] = col.id;
  }
  const compositeToMemberIds: Record<string, string[]> = {};
  const memberIdToCompositeId: Record<string, string> = {};
  for (const col of columns) {
    const members = col.headerFilter?.orGroupFields;
    if (!members || members.length === 0) continue;
    const memberIds = members
      .map((slug) => slugToColumnId[slug])
      .filter((id): id is string => !!id && id !== col.id);
    compositeToMemberIds[col.id] = memberIds;
    for (const mid of memberIds) memberIdToCompositeId[mid] = col.id;
  }
  return { compositeToMemberIds, memberIdToCompositeId };
}

/**
 * Mapa „id kolumny wygaszonej → filtr grupy do echa". Kolumna złożona aktywna → jej pola składowe
 * wygaszone (echo = filtr złożony); żadne pole aktywne → jeśli pole składowe ma filtr, kolumna
 * złożona wygaszona (echo = filtr pola). Przy poprawnym `clearOrGroupPeers` naraz aktywna jest
 * tylko jedna strona, więc echo jest jednoznaczne.
 */
export function computeOrGroupDisabled(
  relations: OrGroupRelations,
  headerFilters: ColumnHeaderFilters,
): Record<string, ColumnHeaderFilter> {
  const map: Record<string, ColumnHeaderFilter> = {};
  for (const [compositeId, memberIds] of Object.entries(relations.compositeToMemberIds)) {
    const compositeFilter = headerFilters[compositeId];
    if (compositeFilter) {
      for (const mid of memberIds) map[mid] = compositeFilter;
    } else {
      const activeMemberId = memberIds.find((mid) => headerFilters[mid]);
      if (activeMemberId) map[compositeId] = headerFilters[activeMemberId]!;
    }
  }
  return map;
}

/**
 * Usuwa z `filters` (mutuje przekazaną kopię i ją zwraca) wpisy SĄSIADÓW grupy OR przy zapisie
 * filtra kolumny `columnId`: zapis kolumny złożonej czyści jej pola składowe; zapis pola składowego
 * czyści kolumnę złożoną. Bez tego sąsiad zostałby w stanie i doleciał do zapytania (podwójne
 * filtrowanie). Wołane WYŁĄCZNIE przy ustawianiu filtra (nie przy czyszczeniu).
 */
export function clearOrGroupPeers(
  filters: ColumnHeaderFilters,
  relations: OrGroupRelations,
  columnId: string,
): ColumnHeaderFilters {
  for (const peerId of relations.compositeToMemberIds[columnId] ?? []) delete filters[peerId];
  const compositeId = relations.memberIdToCompositeId[columnId];
  if (compositeId) delete filters[compositeId];
  return filters;
}
